import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  searchMasstimes, 
  importMasstimesParishes, 
  getParishClaims, 
  approveParishClaim, 
  rejectParishClaim,
  diagnoseImportedParishes,
  repairImportedParishes,
  resyncAllParishes
} from '../lib/api';
import axios from 'axios';
import { 
  Search, 
  MapPin, 
  Church, 
  Clock, 
  Phone, 
  Globe, 
  Download, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Loader2,
  Users,
  Mail,
  MessageSquare,
  Wrench,
  Activity
} from 'lucide-react';

export default function MasstimesImportPage() {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedParishes, setSelectedParishes] = useState(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [claims, setClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [activeTab, setActiveTab] = useState('import');
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [parishCount, setParishCount] = useState(null);

  // Cascading dropdown state
  const [countries, setCountries] = useState([]);
  const [dioceses, setDioceses] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDiocese, setSelectedDiocese] = useState('');
  const [searchStatus, setSearchStatus] = useState('');

  const loadClaims = useCallback(async () => {
    setLoadingClaims(true);
    try {
      const response = await getParishClaims('pending');
      setClaims(response.data.claims || []);
    } catch (error) {
      console.error('Failed to load claims:', error);
    }
    setLoadingClaims(false);
  }, []);

  const loadParishCount = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/parishes/count`);
      setParishCount(response.data.count);
    } catch {}
  }, []);

  useEffect(() => {
    loadParishCount();
    // Load diocese data
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/parishes/dioceses`).then(res => {
      setCountries(res.data.countries || []);
    }).catch(() => {});
  }, [loadParishCount]);

  // Update dioceses when country changes
  useEffect(() => {
    if (selectedCountry) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/parishes/dioceses`).then(res => {
        const byCountry = res.data.dioceses_by_country || {};
        setDioceses(byCountry[selectedCountry] || []);
        setSelectedDiocese('');
      }).catch(() => {});
    } else {
      setDioceses([]);
      setSelectedDiocese('');
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (activeTab === 'claims') {
      loadClaims();
    }
  }, [activeTab, loadClaims]);

  const handleSearch = async (fetchAll = false) => {
    if (!selectedDiocese) {
      toast.error('Please select a diocese');
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    setSelectedParishes(new Set());
    setSearchStatus('Geocoding diocese location...');

    try {
      // Extract the city name from the diocese name (e.g., "Diocese of Memphis" → "Memphis")
      const cityName = selectedDiocese
        .replace(/^(Arch)?[Dd]iocese of\s*/i, '')
        .replace(/\s*\(.*\)/, '')
        .trim();
      
      const searchTerm = `${cityName}, ${selectedCountry}`;
      setSearchStatus(`Searching for "${searchTerm}"...`);

      const geocodeResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=1`,
        { headers: { 'User-Agent': 'Parish.app' } }
      );
      const geocodeData = await geocodeResponse.json();

      if (!geocodeData || geocodeData.length === 0) {
        toast.error(`Could not geocode "${searchTerm}". Try a different diocese.`);
        setIsSearching(false);
        setSearchStatus('');
        return;
      }

      const { lat, lon } = geocodeData[0];
      setSearchStatus(fetchAll ? 'Fetching all pages from MassTimes.org...' : 'Fetching parishes...');
      
      const response = await searchMasstimes(parseFloat(lat), parseFloat(lon), 1, fetchAll);
      setSearchResults(response.data.churches || []);
      
      if (response.data.churches?.length === 0) {
        toast.info('No churches found near this diocese');
      } else {
        const pageInfo = fetchAll ? ` (${response.data.pages_fetched} pages)` : '';
        toast.success(`Found ${response.data.churches.length} churches${pageInfo}`);
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
    }
    setIsSearching(false);
    setSearchStatus('');
  };

  const toggleParish = (index) => {
    const newSelected = new Set(selectedParishes);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedParishes(newSelected);
  };

  const selectAll = () => {
    const importable = searchResults
      .map((p, i) => ({ parish: p, index: i }))
      .filter(({ parish }) => !parish.already_imported);
    
    if (selectedParishes.size === importable.length) {
      setSelectedParishes(new Set());
    } else {
      setSelectedParishes(new Set(importable.map(({ index }) => index)));
    }
  };

  const handleImport = async () => {
    if (selectedParishes.size === 0) {
      toast.error('Please select at least one parish to import');
      return;
    }

    setIsImporting(true);
    try {
      // Send full church data for selected parishes
      const churchesToImport = Array.from(selectedParishes)
        .map(index => searchResults[index])
        .filter(Boolean)
        .map(parish => ({
          masstimes_id: parish.masstimes_id,
          name: parish.name,
          address: parish.address,
          diocese_name: parish.diocese_name,
          office_phone: parish.office_phone,
          website: parish.website,
          latitude: parish.latitude,
          longitude: parish.longitude,
          mass_schedules: parish.mass_schedules || [],
          reconciliation_schedules: parish.reconciliation_schedules || [],
          adoration_schedules: parish.adoration_schedules || []
        }));

      const response = await importMasstimesParishes(churchesToImport);
      
      toast.success(`Imported ${response.data.imported} parishes!`);
      loadParishCount();
      
      // Update search results to show imported status
      const importedIds = new Set(response.data.parishes?.map(p => p.masstimes_id) || []);
      setSearchResults(prev => prev.map(p => ({
        ...p,
        already_imported: p.already_imported || importedIds.has(p.masstimes_id)
      })));
      
      setSelectedParishes(new Set());
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    }
    setIsImporting(false);
  };

  const handleApproveClaim = async (claimId) => {
    try {
      await approveParishClaim(claimId);
      toast.success('Claim approved! User is now parish admin.');
      loadClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve claim');
    }
  };

  const handleRejectClaim = async () => {
    if (!selectedClaim) return;
    
    try {
      await rejectParishClaim(selectedClaim.claim_id, rejectReason);
      toast.success('Claim rejected');
      setRejectDialogOpen(false);
      setSelectedClaim(null);
      setRejectReason('');
      loadClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject claim');
    }
  };

  const handleDiagnose = async () => {
    setIsDiagnosing(true);
    try {
      const response = await diagnoseImportedParishes();
      setDiagnosisResult(response.data);
      if (response.data.with_issues > 0) {
        toast.warning(`Found ${response.data.with_issues} parishes with issues`);
      } else {
        toast.success(`All ${response.data.healthy} imported parishes are healthy!`);
      }
    } catch (error) {
      console.error('Diagnose error:', error);
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      
      if (status === 404) {
        toast.error('Endpoint not found (404). The backend may need to be restarted after deployment.');
      } else if (status === 403) {
        toast.error('Access denied. Site admin privileges required.');
      } else if (status === 500) {
        toast.error(`Server error: ${errorMsg}`);
      } else {
        toast.error(`Error (${status || 'network'}): ${errorMsg}`);
      }
    }
    setIsDiagnosing(false);
  };

  const handleRepair = async () => {
    setIsRepairing(true);
    try {
      const response = await repairImportedParishes();
      toast.success(`Repaired ${response.data.repaired} parishes!`);
      // Re-run diagnosis after repair
      handleDiagnose();
    } catch (error) {
      toast.error('Failed to repair parishes');
    }
    setIsRepairing(false);
  };

  const handleResyncAll = async () => {
    setIsResyncing(true);
    setResyncResult(null);
    try {
      const response = await resyncAllParishes();
      setResyncResult(response.data);
      toast.success(`Re-synced ${response.data.synced} parishes from MassTimes.org`);
      loadParishCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Re-sync failed');
    }
    setIsResyncing(false);
  };


  const importableCount = searchResults.filter(p => !p.already_imported).length;

  return (
    <div className="min-h-screen bg-[#F5F3EE] py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#2C2925] font-['Nunito']">
                Parish Import & Management
              </h1>
              <p className="text-[#6B665E] mt-2">
                Import parishes from MassTimes.org and manage parish claim requests
              </p>
            </div>
            <div className="bg-white border border-[#E5E0D5] rounded-lg px-5 py-3 text-center shadow-sm" data-testid="parish-count-card">
              <div className="text-3xl font-bold text-[var(--liturgical-primary)] font-['Crimson_Text']">
                {parishCount !== null ? parishCount : '...'}
              </div>
              <div className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5">Parishes in Database</div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Import Parishes
            </TabsTrigger>
            <TabsTrigger value="claims" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Claim Requests
              {claims.length > 0 && (
                <Badge variant="destructive" className="ml-1">{claims.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="diagnose" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Diagnose & Repair
            </TabsTrigger>
          </TabsList>

          <TabsContent value="import">
            {/* Search Section */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Browse by Diocese
                </CardTitle>
                <CardDescription>
                  Select a country and diocese to find and import parishes from MassTimes.org
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Country dropdown */}
                  <div className="flex-1">
                    <label className="text-xs font-medium text-[#2C2925] mb-1 block font-['Nunito']">Country</label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)]"
                      data-testid="country-select"
                    >
                      <option value="">Select country...</option>
                      {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  
                  {/* Diocese dropdown */}
                  <div className="flex-1">
                    <label className="text-xs font-medium text-[#2C2925] mb-1 block font-['Nunito']">Diocese</label>
                    <select
                      value={selectedDiocese}
                      onChange={(e) => setSelectedDiocese(e.target.value)}
                      disabled={!selectedCountry}
                      className="w-full h-10 px-3 rounded-md border border-[#E5E0D5] bg-white text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[var(--liturgical-primary)] disabled:opacity-50"
                      data-testid="diocese-select"
                    >
                      <option value="">{selectedCountry ? 'Select diocese...' : 'Select a country first'}</option>
                      {dioceses.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-2 items-end">
                    <Button 
                      onClick={() => handleSearch(false)} 
                      disabled={isSearching || !selectedDiocese}
                      variant="outline"
                      data-testid="masstimes-search-btn"
                    >
                      {isSearching ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Preview (30)
                    </Button>
                    <Button 
                      onClick={() => handleSearch(true)} 
                      disabled={isSearching || !selectedDiocese}
                      data-testid="masstimes-fetch-all-btn"
                    >
                      {isSearching ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Fetch All
                    </Button>
                  </div>
                </div>
                {searchStatus && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-[#6B665E] font-['Nunito']">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {searchStatus}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results Section */}
            {searchResults.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Search Results</CardTitle>
                      <CardDescription>
                        {searchResults.length} parishes found • {importableCount} available to import
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={selectAll}
                        disabled={importableCount === 0}
                      >
                        {selectedParishes.size === importableCount ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button 
                        onClick={handleImport}
                        disabled={selectedParishes.size === 0 || isImporting}
                        data-testid="import-selected-btn"
                      >
                        {isImporting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Import Selected ({selectedParishes.size})
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {searchResults.map((parish, index) => (
                      <div 
                        key={index}
                        className={`p-4 border rounded-lg transition-colors ${
                          parish.already_imported 
                            ? 'bg-green-50 border-green-200' 
                            : selectedParishes.has(index)
                              ? 'bg-amber-50 border-amber-300'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          {!parish.already_imported && (
                            <Checkbox
                              checked={selectedParishes.has(index)}
                              onCheckedChange={() => toggleParish(index)}
                              className="mt-1"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Church className="w-5 h-5 text-[#8B7355]" />
                              <h3 className="font-semibold text-[#2C2925]">{parish.name}</h3>
                              {parish.already_imported && (
                                <Badge variant="secondary" className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Already Imported
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#6B665E]">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                <span>{parish.address}</span>
                              </div>
                              {parish.diocese_name && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Diocese:</span>
                                  <span>{parish.diocese_name}</span>
                                </div>
                              )}
                              {parish.office_phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4" />
                                  <span>{parish.office_phone}</span>
                                </div>
                              )}
                              {parish.website && (
                                <div className="flex items-center gap-2">
                                  <Globe className="w-4 h-4" />
                                  <a 
                                    href={parish.website.startsWith('http') ? parish.website : `https://${parish.website}`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline truncate"
                                  >
                                    {parish.website}
                                  </a>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>{parish.mass_schedules?.length || 0} Mass times</span>
                              </div>
                              {parish.distance_miles > 0 && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                    {parish.distance_miles.toFixed(1)} mi away
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="claims">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Pending Parish Claims
                </CardTitle>
                <CardDescription>
                  Review and approve requests from users who want to manage parishes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingClaims ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#8B7355]" />
                  </div>
                ) : claims.length === 0 ? (
                  <div className="text-center py-8 text-[#6B665E]">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending parish claims</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {claims.map((claim) => (
                      <div 
                        key={claim.claim_id}
                        className="p-4 border rounded-lg bg-white"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-[#2C2925] mb-1">
                              {claim.parish_name}
                            </h3>
                            <div className="space-y-1 text-sm text-[#6B665E]">
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span>Requested by: <strong>{claim.user_name}</strong></span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                <span>{claim.user_email}</span>
                              </div>
                              {claim.role_at_parish && (
                                <div className="flex items-center gap-2">
                                  <Church className="w-4 h-4" />
                                  <span>Role: {claim.role_at_parish}</span>
                                </div>
                              )}
                              <div className="flex items-start gap-2 mt-2">
                                <MessageSquare className="w-4 h-4 mt-0.5" />
                                <div className="bg-gray-50 p-2 rounded flex-1">
                                  <p className="italic">"{claim.message}"</p>
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 mt-2">
                                Submitted: {new Date(claim.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => {
                                setSelectedClaim(claim);
                                setRejectDialogOpen(true);
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveClaim(claim.claim_id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="diagnose">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Diagnose Imported Parishes
                </CardTitle>
                <CardDescription>
                  Check for issues with imported parishes (missing coordinates, mass schedules, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 mb-6">
                  <Button 
                    onClick={handleDiagnose} 
                    disabled={isDiagnosing}
                    variant="outline"
                  >
                    {isDiagnosing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Activity className="w-4 h-4 mr-2" />
                    )}
                    Run Diagnosis
                  </Button>
                  {diagnosisResult?.with_issues > 0 && (
                    <Button 
                      onClick={handleRepair} 
                      disabled={isRepairing}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {isRepairing ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Wrench className="w-4 h-4 mr-2" />
                      )}
                      Repair Issues
                    </Button>
                  )}
                </div>

                {diagnosisResult && (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-700">{diagnosisResult.total_imported}</div>
                        <div className="text-sm text-blue-600">Total Imported</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-700">{diagnosisResult.healthy}</div>
                        <div className="text-sm text-green-600">Healthy</div>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${diagnosisResult.with_issues > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                        <div className={`text-2xl font-bold ${diagnosisResult.with_issues > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                          {diagnosisResult.with_issues}
                        </div>
                        <div className={`text-sm ${diagnosisResult.with_issues > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          With Issues
                        </div>
                      </div>
                    </div>

                    {/* Issues List */}
                    {diagnosisResult.issues?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-[#2C2925] mb-2">Parishes with Issues:</h3>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {diagnosisResult.issues.map((issue, idx) => (
                            <div key={idx} className="bg-red-50 border border-red-200 p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-red-800">{issue.name}</span>
                                <div className="flex gap-1">
                                  {issue.issues.map((i, iIdx) => (
                                    <Badge key={iIdx} variant="secondary" className="bg-red-100 text-red-700 text-xs">
                                      {i.replace(/_/g, ' ')}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              <div className="text-xs text-red-600 mt-1">
                                Mass schedules: {issue.mass_schedule_count} | Has lat/lng: {issue.has_lat_lng ? 'Yes' : 'No'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Healthy List */}
                    {diagnosisResult.healthy_parishes?.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-[#2C2925] mb-2">Sample Healthy Parishes:</h3>
                        <div className="space-y-2">
                          {diagnosisResult.healthy_parishes.map((parish, idx) => (
                            <div key={idx} className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-center justify-between">
                              <span className="text-green-800">{parish.name}</span>
                              <span className="text-xs text-green-600">{parish.mass_schedule_count} mass times</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!diagnosisResult && (
                  <div className="text-center py-8 text-[#6B665E]">
                    <Activity className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Click "Run Diagnosis" to check imported parishes for issues</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Re-sync Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Re-sync Schedules from MassTimes.org
                </CardTitle>
                <CardDescription>
                  Re-fetch mass, reconciliation, and adoration schedules for all imported parishes. 
                  Use this to fix missing adoration times or update schedules.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={handleResyncAll} 
                  disabled={isResyncing}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90"
                  data-testid="resync-all-btn"
                >
                  {isResyncing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {isResyncing ? 'Re-syncing...' : 'Re-sync All Imported Parishes'}
                </Button>

                {resyncResult && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <div className="text-xl font-bold text-blue-700">{resyncResult.total}</div>
                        <div className="text-xs text-blue-600">Total</div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg text-center">
                        <div className="text-xl font-bold text-green-700">{resyncResult.synced}</div>
                        <div className="text-xs text-green-600">Synced</div>
                      </div>
                      {resyncResult.errors > 0 && (
                        <div className="bg-red-50 p-3 rounded-lg text-center">
                          <div className="text-xl font-bold text-red-700">{resyncResult.errors}</div>
                          <div className="text-xs text-red-600">Errors</div>
                        </div>
                      )}
                    </div>
                    {resyncResult.synced_parishes?.length > 0 && (
                      <div className="max-h-[300px] overflow-y-auto space-y-1">
                        {resyncResult.synced_parishes.map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-green-50 border border-green-200 p-2 rounded text-sm">
                            <span className="text-green-800 font-medium">{p.name}</span>
                            <div className="flex gap-2 text-xs text-green-600">
                              <span>{p.mass} Mass</span>
                              <span>{p.reconciliation} Conf</span>
                              <span>{p.adoration} Ador</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Parish Claim</DialogTitle>
              <DialogDescription>
                Are you sure you want to reject this claim from {selectedClaim?.user_name} for {selectedClaim?.parish_name}?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium text-[#2C2925]">
                Reason for rejection (optional)
              </label>
              <Textarea
                placeholder="Provide a reason for rejecting this claim..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-2"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleRejectClaim}
              >
                Reject Claim
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
